<template>
  <el-drawer
    :visible.sync="drawerVisible"
    title="订单详情"
    size="600px"
    direction="rtl"
    custom-class="order-drawer"
    @close="handleClose">
    <div class="drawer-content" v-loading="loading">
      <!-- 订单状态头部 -->
      <div class="drawer-status-bar" :class="getStatusBarClass(orderData.orderStatus)">
        <div class="status-main">
          <el-tag :type="getOrderStatusType(orderData.orderStatus)" size="medium" effect="dark">
            {{ orderData.orderStatus | filterOrderStatus }}
          </el-tag>
          <span class="order-no-text">{{ orderData.orderNo }}</span>
          <el-button type="text" icon="el-icon-document-copy" @click="$utils.copyText(orderData.orderNo)"></el-button>
        </div>
        <div class="status-time">下单时间：{{ orderData.createTime | filterTime }}</div>
      </div>

      <!-- 金额概览 -->
      <div class="drawer-section amount-section">
        <div class="amount-card">
          <div class="amount-item">
            <span class="amount-label">订单金额</span>
            <span class="amount-value order">¥{{ orderData.orderAmount | filterMoney }}</span>
          </div>
          <div class="amount-divider">
            <i class="el-icon-right"></i>
          </div>
          <div class="amount-item">
            <span class="amount-label">佣金金额</span>
            <span class="amount-value commission">¥{{ orderData.commissionAmount | filterMoney }}</span>
          </div>
          <!-- 固定金额时显示佣金类型，比例时显示佣金比例 -->
          <div class="amount-item rate" v-if="orderData.commissionType === 1">
            <span class="amount-label">佣金类型</span>
            <span class="amount-value">{{ orderData.commissionType | filterConst('CHANNEL_COMMISSION_TYPE') }}</span>
          </div>
          <div class="amount-item rate" v-else>
            <span class="amount-label">佣金比例</span>
            <span class="amount-value">{{ calculateRate(orderData) }}%</span>
          </div>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="drawer-section">
        <div class="section-header">
          <i class="el-icon-document"></i>
          <span>订单信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">线路名称</span>
            <span class="info-value">{{ orderData.lineName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">支付方式</span>
            <span class="info-value">{{ orderData.paymentType | filterConst('CHANNEL_PAYMENT_CHANNEL') }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">支付时间</span>
            <span class="info-value">{{ orderData.payTime | filterTime }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">订单来源</span>
            <span class="info-value">渠道推广</span>
          </div>
        </div>
      </div>

      <!-- 渠道信息 -->
      <div class="drawer-section">
        <div class="section-header">
          <i class="el-icon-user"></i>
          <span>渠道信息</span>
          <el-button type="text" size="mini" @click="handleGoToChannel">查看渠道</el-button>
        </div>
        <div class="channel-info-card">
          <div class="channel-avatar">
            <i class="el-icon-user"></i>
          </div>
          <div class="channel-detail">
            <div class="channel-name">{{ orderData.channelName }}</div>
            <div class="channel-meta">
              <span class="channel-no">{{ orderData.channelNo }}</span>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="$utils.copyText(orderData.channelNo)"></el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 乘客信息 -->
      <div class="drawer-section">
        <div class="section-header">
          <i class="el-icon-s-custom"></i>
          <span>乘客信息</span>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">乘客姓名</span>
            <span class="info-value ">{{ orderData.passengerName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">联系电话</span>
            <span class="info-value">
              {{ orderData.phone }}
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="$utils.copyText(orderData.phone)"></el-button>
            </span>
          </div>
        </div>
      </div>

      <!-- 佣金信息 -->
      <div class="drawer-section">
        <div class="section-header">
          <i class="el-icon-coin"></i>
          <span>佣金信息</span>
        </div>
        <div class="commission-info">
          <div class="commission-config">
            <div class="config-item">
              <span class="config-label">佣金类型</span>
              <el-tag :type="orderData.commissionType === 1 ? 'primary' : 'success'" size="small">
                {{ orderData.commissionType | filterConst('CHANNEL_COMMISSION_TYPE') }}
              </el-tag>
            </div>
            <div class="config-item">
              <span class="config-label">佣金配置</span>
              <span class="config-value">{{ orderData.commissionValue }}{{ orderData.commissionType === 1 ? '元/单' : '%' }}</span>
            </div>
            <div class="config-item">
              <span class="config-label">佣金状态</span>
              <el-tag :type="getCommissionStatusType(orderData.commissionStatus)" size="small">
                {{ orderData.commissionStatus | filterConst('CHANNEL_COMMISSION_STATUS') }}
              </el-tag>
            </div>
          </div>
          <div class="commission-calc">
            <div class="calc-formula" v-if="orderData.commissionType === 2">
              <span>¥{{ orderData.orderAmount | filterMoney }}</span>
              <span class="calc-symbol">×</span>
              <span>{{ orderData.commissionValue }}%</span>
              <span class="calc-symbol">=</span>
              <span class="calc-result">¥{{ orderData.commissionAmount | filterMoney }}</span>
            </div>
            <div class="calc-formula" v-else>
              <span>固定佣金</span>
              <span class="calc-symbol">=</span>
              <span class="calc-result">¥{{ orderData.commissionAmount | filterMoney }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 时间线 -->
      <div class="drawer-section">
        <div class="section-header">
          <i class="el-icon-time"></i>
          <span>订单时间线</span>
        </div>
        <el-timeline>
          <el-timeline-item
            :type="orderData.createTime ? 'primary' : 'info'"
            :icon="orderData.createTime ? 'el-icon-check' : 'el-icon-time'">
            <div class="timeline-title">订单创建</div>
            <div class="timeline-time" v-if="orderData.createTime">{{ formatTime(orderData.createTime) }}</div>
          </el-timeline-item>
          <el-timeline-item

            :type="orderData.payTime ? 'primary' : 'info'"
            :icon="orderData.payTime ? 'el-icon-check' : 'el-icon-time'">
            <div class="timeline-title">支付完成</div>
            <div class="timeline-time" v-if="orderData.payTime">{{ formatTime(orderData.payTime) }}</div>
          </el-timeline-item>
          <el-timeline-item

            :type="orderData.completeTime ? 'primary' : 'info'"
            :icon="orderData.completeTime ? 'el-icon-check' : 'el-icon-time'">
            <div class="timeline-title">订单完成</div>
            <div class="timeline-time" v-if="orderData.completeTime">{{ formatTime(orderData.completeTime) }}</div>
          </el-timeline-item>
          <el-timeline-item
            :type="orderData.settlementTime ? 'primary' : 'info'"
            :icon="orderData.settlementTime ? 'el-icon-check' : 'el-icon-time'">
            <div class="timeline-title">佣金结算</div>
            <div class="timeline-time" v-if="orderData.settlementTime">{{ formatTime(orderData.settlementTime) }}</div>
          </el-timeline-item>
        </el-timeline>
      </div>
    </div>

    <!-- 抽屉底部 -->
    <div class="drawer-footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>
  </el-drawer>
</template>

<script>
export default {
  name: 'OrderDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: [Number, String],
      default: null
    },
    // 调用来源类型：'financial' 表示财务模块调用，其他表示订单模块调用
    callerType: {
      type: String,
      default: 'order'
    }
  },
  data() {
    return {
      loading: false,
      orderData: {}
    }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.orderId) {
        this.loadDetail()
      }
    },
    orderId(val) {
      if (val && this.visible) {
        this.loadDetail()
      }
    }
  },
  methods: {
    /**
     * 获取订单详情
     * 使用接口：GET /api/v1/channel/order/detail
     * 作者：孙艺
     */
    async loadDetail() {
      if (!this.orderId) {
        return
      }

      this.loading = true
      try {
        // 财务模块调用时，URL 后面加 /byOrderId，参数字段使用 orderId
        const apiUrl = this.callerType === 'financial' 
          ? '/api/v1/channel/order/detail/byOrderId'
          : '/api/v1/channel/order/detail'
        const params = this.callerType === 'financial' 
          ? { orderId: this.orderId }
          : { id: this.orderId }
        const res = await this.$axios.get(apiUrl, params)
        if (res && res.code === 1 && res.data) {
          const detail = res.data
          // 字段映射：接口返回的字段名 -> 模板使用的字段名
          this.orderData = {
            ...detail,
            createTime: detail.orderTime, // 下单时间
            payTime: detail.paymentTime, // 支付时间
            completeTime: detail.finishTime, // 完单时间
            paymentType: detail.payChannel // 支付方式（字符串枚举）
          }
        }
      } catch (error) {
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败')
      } finally {
        this.loading = false
      }
    },
    /**
     * 格式化时间戳
     * 使用 utils.js 的 timeFn 方法
     * 作者：孙艺
     */
    formatTime(timestamp, format = 'YYYY/MM/DD HH:mm:SS') {
      return this.$utils.timeFn(timestamp, format)
    },
    /**
     * 关闭抽屉
     * 作者：孙艺
     */
    handleClose() {
      this.$emit('update:visible', false)
    },
    /**
     * 跳转到渠道详情
     * 作者：孙艺
     */
    handleGoToChannel() {
      this.$emit('go-to-channel', this.orderData)
    },
    /**
     * 获取订单状态类型
     * 作者：孙艺
     */
    getOrderStatusType(status) {
      const statusItem = this.$const.ORDERSTATUS.find(item => {
        if (item.value === status) return true
        if (typeof item.value === 'string' && item.value.includes(',')) {
          return item.value.split(',').map(Number).includes(status)
        }
        return false
      })
      return statusItem ? statusItem.type : ''
    },
    /**
     * 获取状态栏样式类
     * 作者：孙艺
     */
    getStatusBarClass(status) {
      if (status === 3) return 'completed'
      if (status === 2) return 'paid'
      if (status === 1) return 'pending'
      return 'cancelled'
    },
    /**
     * 获取佣金状态类型
     * 作者：孙艺
     */
    getCommissionStatusType(status) {
      const statusItem = this.$const.CHANNEL_COMMISSION_STATUS.find(i => i.value === status)
      return statusItem ? statusItem.type : ''
    },
    /**
     * 计算佣金比例
     * 作者：孙艺
     */
    calculateRate(row) {
      if (!row.orderAmount || row.orderAmount === 0) return '0.00'
      return ((row.commissionAmount / row.orderAmount) * 100).toFixed(2)
    }
  }
}
</script>

<style lang="less" scoped>
// 抽屉样式
/deep/ .order-drawer {
  .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid #EBEEF5;
  }

  .el-drawer__body {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: calc(100% - 60px);
  }
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 80px;
}

// 状态栏
.drawer-status-bar {
  padding: 16px 20px;

  &.completed {
    background: linear-gradient(135deg, #67C23A15 0%, #67C23A08 100%);
    border-bottom: 2px solid #67C23A;
  }

  &.paid {
    background: linear-gradient(135deg, #409EFF15 0%, #409EFF08 100%);
    border-bottom: 2px solid #409EFF;
  }

  &.pending {
    background: linear-gradient(135deg, #E6A23C15 0%, #E6A23C08 100%);
    border-bottom: 2px solid #E6A23C;
  }

  &.cancelled {
    background: linear-gradient(135deg, #90939915 0%, #90939908 100%);
    border-bottom: 2px solid #909399;
  }

  .status-main {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    .order-no-text {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
    }
  }

  .status-time {
    font-size: 13px;
    color: #909399;
  }
}

// 金额概览
.amount-section {
  padding: 20px;

  .amount-card {
    display: flex;
    align-items: center;
    background: #f5f7fa;
    border-radius: 8px;
    padding: 16px;

    .amount-item {
      flex: 1;
      text-align: center;

      .amount-label {
        display: block;
        font-size: 12px;
        color: #909399;
        margin-bottom: 8px;
      }

      .amount-value {
        display: block;
        font-size: 18px;
        font-weight: 600;
        color: #303133;

        &.order {
          color: #E6A23C;
        }

        &.commission {
          color: #67C23A;
        }
      }

      &.rate {
        .amount-value {
          color: #409EFF;
        }
      }
    }

    .amount-divider {
      width: 40px;
      text-align: center;
      color: #C0C4CC;
    }
  }
}

.drawer-section {
  padding: 20px;
  border-bottom: 1px solid #EBEEF5;

  &:last-child {
    border-bottom: none;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 16px;

    i {
      color: #409EFF;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .info-label {
        font-size: 12px;
        color: #909399;
      }

      .info-value {
        font-size: 14px;
        color: #303133;

        &.highlight {
          color: #409EFF;
          font-weight: 500;
        }
      }
    }
  }

  .channel-info-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 6px;

    .channel-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 18px;
    }

    .channel-detail {
      flex: 1;

      .channel-name {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 4px;
      }

      .channel-meta {
        display: flex;
        align-items: center;
        gap: 8px;

        .channel-no {
          font-size: 12px;
          color: #909399;
        }
      }
    }
  }

  .commission-info {
    .commission-config {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 16px;

      .config-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .config-label {
          font-size: 13px;
          color: #606266;
        }

        .config-value {
          margin-left: 6px;
          color: #606266;
        }
      }
    }

    .commission-calc {
      padding: 12px;
      background: #f5f7fa;
      border-radius: 6px;

      .calc-formula {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
        color: #606266;

        .calc-symbol {
          color: #909399;
        }

        .calc-result {
          color: #67C23A;
          font-weight: 600;
          font-size: 16px;
        }
      }
    }
  }

  .timeline-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
  }
}

.drawer-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid #EBEEF5;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  z-index: 10;
}
</style>

